import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { FormacaoService as FormacaoTiposService } from './formacao.service';
import { FormacaoService as FormacaoCoreService } from '@/core/api/sicom-api/registro/medico/formacao/formacao.service';
import { EscolaService } from '@/core/api/sicom-api/registro/medico/escola/escola.service';
import { TipoDiplomaResponse, TipoFormacaoResponse } from './formacao.model';
import { FormacaoRequest } from '@/core/api/sicom-api/registro/medico/formacao/formacao.model';
import { PosGraduacaoResponse } from '@/core/api/sicom-api/registro/medico/formacao/pos-graduacao.model';
import { CursosPosEntityResponse } from '@/core/api/sicom-api/registro/medico/escola/escola-cursos-pos-entity.model';

@Injectable({ providedIn: 'root' })
/** Fachada de opções de formação (tipos, diplomas, comprovantes, cursos de pós) como Observables. */
export class FormacaoFacade {
    constructor(
        private readonly formacaoTiposService: FormacaoTiposService,
        private readonly formacaoCoreService: FormacaoCoreService,
        private readonly escolaService: EscolaService
    ) {}

    // Helpers
    private parseIsoDate(value?: string): number {
        if (!value) return 0;
        const t = Date.parse(value);
        return isNaN(t) ? 0 : t;
    }

    tiposFormacaoOptions$(): Observable<Array<{ label: string; value: number }>> {
        return this.formacaoTiposService.listarTiposFormacao().pipe(map((lista: TipoFormacaoResponse[] = []) => lista.map((item) => ({ label: item.nmTipoFormacao, value: item.idTipoFormacao }))));
    }

    diplomasPorTipoOptions$(idTipoFormacao: number): Observable<Array<{ label: string; value: number; idTipoDiploma?: number }>> {
        // Usa o serviço core, que já centraliza o PATH correto para diplomas por tipo
        return this.formacaoCoreService
            .listarDiplomasPorTipoFormacao(idTipoFormacao)
            .pipe(
                map((lista: Array<{ nmTipoDiploma?: string; idTipoFormacaoDiploma?: number; idTipoDiploma?: number }> = []) =>
                    lista.map((item) => ({ label: item.nmTipoDiploma ?? '', value: Number(item.idTipoFormacaoDiploma ?? item.idTipoDiploma), idTipoDiploma: Number(item.idTipoDiploma) })).filter((item) => Number.isFinite(item.value))
                )
            );
    }

    comprovantesPosOptions$(): Observable<Array<{ label: string; value: number; idTipoDiploma?: number }>> {
        return this.formacaoCoreService
            .listarComprovantePosGraduacao()
            .pipe(
                map((lista: Array<{ nmTipoDiploma?: string; idTipoFormacaoDiploma?: number; idTipoDiploma?: number }> = []) =>
                    lista.map((item) => ({ label: item.nmTipoDiploma ?? '', value: Number(item.idTipoFormacaoDiploma ?? item.idTipoDiploma), idTipoDiploma: Number(item.idTipoDiploma) }))
                )
            );
    }

    formacoesOrdenadas$(pessoa: Pessoa): Observable<FormacaoRequest[]> {
        return this.formacaoCoreService.buscar(pessoa).pipe(map((lista: FormacaoRequest[] = []) => lista.slice().sort((a, b) => this.parseIsoDate(b.dtFormatura) - this.parseIsoDate(a.dtFormatura))));
    }

    posGraduacoesOrdenadas$(pessoa: Pessoa): Observable<PosGraduacaoResponse[]> {
        return this.formacaoCoreService.listarPosGraduacoes(pessoa).pipe(
            map((lista: PosGraduacaoResponse[] = []) => {
                const score = (r: PosGraduacaoResponse) => this.parseIsoDate(r.dtFormatura) || this.parseIsoDate(r.dtFim) || this.parseIsoDate(r.dtInicio) || this.parseIsoDate(r.dtInicioOferta);
                return lista.slice().sort((a, b) => score(b) - score(a));
            })
        );
    }

    cursosPosPorIes$(coIes: number): Observable<{ lista: CursosPosEntityResponse[]; options: Array<{ label: string; value: number }> }> {
        return this.escolaService.listarCursosPosPorIes(coIes).pipe(
            map((lista: CursosPosEntityResponse[] = []) => ({
                lista,
                options: lista.filter((c) => c.coCursoEspecializacao != null && !!c.nmCursoEspecializacao).map((c) => ({ label: String(c.nmCursoEspecializacao), value: Number(c.coCursoEspecializacao) }))
            }))
        );
    }
}
